import React, { FC, ReactEventHandler, ReactNode } from 'react'
import { Font } from 'is24-corecss'
import { useInView } from 'react-intersection-observer'
import { useSelector } from 'react-redux'
import './StepTemplate.scss'
import { FontsProps } from 'is24-corecss/build/@types/Fonts/FontsProps'
import BackButton from '../BackButton/BackButton'
import { AnimationClass } from '../../@types/animation'
import { Progress } from '../../@types/progress'
import { DEFAULT_PROGRESS } from '../../progress/progressHelper'
import { DEFAULT_PROGRESS_VALUATION } from '../../progress/valuationProgressHelper'
import ValuationBar from '../ValuationBar/ValuationBar'
import { ValuationBarProgress } from '../../@types/valuationBarProgress'
import { RealEstateType } from '../../@types/realEstateType'
import { WizardFlowType } from '../../@types/wizardFlowType'
import { ProgressBar } from '../styled/ProgressBar/ProgressBar'
import NextButton from '../NextButton/NextButton'
import { IntegrationType } from '../../@types/config'
import { ReduxState } from '../../store/reducers'
import { getIsPdfWordingEnabled } from '../../utils/pdf'

export interface TemplateProps {
  hideIconOnMobile?: boolean
  header?: ReactNode
  headline: ReactNode
  headlineClassname?: string
  footer?: ReactNode
  highlighter?: boolean
  subLine?: ReactNode
  icon?: ReactNode
  subIconContent?: ReactNode
  displayNext?: boolean
  displayBack?: boolean
  isNextEnabled?: boolean
  goToNextStep?: ReactEventHandler
  buttonString?: string
  buttonAppearance?: 'primary' | 'secondary' | 'ghost' | 'inverted' | undefined
  buttonResponsive?: boolean
  goToPreviousStep?: ReactEventHandler
  children?: ReactNode
  dataTestingButton?: string
  progress?: Progress
  valuationBarProgress?: ValuationBarProgress
  className?: string
  isExtendedBuyerLead?: boolean
  noMarginTop?: boolean
  legalNoteContent?: ReactNode
}
export const DEFAULT_LEGAL_NOTE = 'Wir finanzieren diesen Service über die Provision unserer Immobilienprofis'

const StepTemplate: FC<TemplateProps> = (props) => {
  const {
    icon,
    subIconContent,
    hideIconOnMobile = true,
    header,
    headline,
    headlineClassname = '',
    footer,
    highlighter,
    subLine,
    displayNext,
    displayBack,
    isNextEnabled,
    goToNextStep,
    buttonString = 'Weiter',
    buttonAppearance = 'secondary',
    buttonResponsive = false,
    children,
    dataTestingButton,
    goToPreviousStep,
    progress = DEFAULT_PROGRESS,
    className = '',
    isExtendedBuyerLead,
    noMarginTop,
    legalNoteContent,
  } = props
  const { ref, inView } = useInView({})
  const {
    config: { theme, isIframe, integrationType },
    lead: { realEstateType, extendedBuyerLead },
    flow: { wizardFlowType, userFirstInteraction },
    animation: { animationClass },
  } = useSelector((state: ReduxState) => state)

  const isPdfWordingChangeEnabled = getIsPdfWordingEnabled(wizardFlowType, realEstateType)
  const isIv24Integration = integrationType === IntegrationType.IMMOVERKAUF24
  const externalIntegration = integrationType === IntegrationType.EXTERNAL
  const contentAnimationStyles = { opacity: inView ? 1 : 0 }
  const showPDFStatus =
    wizardFlowType === WizardFlowType.IBW &&
    goToPreviousStep &&
    (realEstateType === RealEstateType.HOUSE || realEstateType === RealEstateType.APARTMENT) &&
    !extendedBuyerLead

  let progressAnimationValue = 0
  switch (animationClass) {
    case AnimationClass.BACK:
      progressAnimationValue = inView ? progress.current : progress.next

      break
    case AnimationClass.NEXT:
      progressAnimationValue = inView ? progress.current : progress.previous
      break
  }

  return (
    <div
      className={`${
        isIframe || isIv24Integration
          ? `step-template-iframe grid grid-flex grid-justify-center padding-horizontal-m padding-vertical-m font-antialiased ${className}`
          : `step-template grid grid-flex grid-justify-center desk-padding-horizontal-m lap-padding-horizontal-m padding-vertical-m font-antialiased ${className}`
      }`}
    >
      {(props.valuationBarProgress || props.progress) && (
        <div className='grid-item one-whole align-left padding-bottom-xl'>
          {props.valuationBarProgress ? (
            <div className='grid grid-flex'>
              <div className='grid-item one-sixth margin-top-xs'>
                {displayBack && (
                  <BackButton onClick={goToPreviousStep} iconColor={theme?.secondary} color={theme?.text} />
                )}
              </div>
              <div data-testid='valuation-bar' className='grid-item four-sixth'>
                <ValuationBar
                  progress={props.valuationBarProgress ? props.valuationBarProgress : DEFAULT_PROGRESS_VALUATION}
                />
              </div>
            </div>
          ) : (
            <div className='grid grid-flex'>
              <div className='grid-item one-third back-button-holder'>
                {displayBack && (
                  <BackButton onClick={goToPreviousStep} iconColor={theme?.secondary} color={theme?.text} />
                )}
              </div>
              <div className='grid-item one-third'>
                <div
                  data-testid='progress-bar-wrapper'
                  className={`progress-bar horizontal-center ${
                    props.progress && props.progress.current && props.progress.current > 0 ? '' : 'hide'
                  }`}
                >
                  {showPDFStatus && (
                    <div className='progress-bar-info'>
                      <span>{isPdfWordingChangeEnabled ? 'Bewertung' : 'PDF Erstellung'}</span>
                      <span>{progressAnimationValue}%</span>
                    </div>
                  )}
                  <ProgressBar
                    progress={progressAnimationValue}
                    backgroundColor={theme?.primaryLight}
                    progressColor={theme?.primary}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <div className={`step-template-content ${animationClass} `} ref={ref} style={contentAnimationStyles}>
        {header && (
          <div data-testid='step-template-header' className='grid-item one-whole'>
            {header}
          </div>
        )}
        {icon && (
          <div className={`grid-item one-whole ${hideIconOnMobile ? 'palm-hide' : ''} padding-top-l`}>{icon}</div>
        )}
        {subIconContent && (
          <div className={`grid-item one-whole ${hideIconOnMobile ? 'palm-hide' : ''}`}>{subIconContent}</div>
        )}
        <div id='step-template-headlines' className='grid-item grid padding-bottom-m'>
          <h2
            data-testid='step-template-headline'
            className='grid-item one-whole align-center font-normal font-line-xs palm-font-xl padding-horizontal-l'
            style={{
              color: `${theme?.text ? theme.text : `#333`}`,
            }}
          >
            <span className={`${highlighter ? 'highlighter' : ''} ${headlineClassname}`}>{headline}</span>
          </h2>

          <div className='grid-item one-whole align-center font-normal '>
            <p data-testid='step-template-sub-line'>{subLine}</p>
          </div>
        </div>
        <div
          id='step-template-children'
          className={`grid-item one-whole input-text-container ${
            isExtendedBuyerLead
              ? 'step-container-value-proposition'
              : noMarginTop
              ? 'step-container-no-margin-top'
              : 'step-container'
          } align-center padding-horizontal-l`}
        >
          {children}
        </div>

        {displayNext && (
          <NextButton
            buttonText={buttonString}
            buttonAppearance={buttonAppearance}
            responsive={buttonResponsive}
            onClick={goToNextStep}
            dataTestingButton={dataTestingButton}
            disabled={!isNextEnabled}
            customClasses={'step-template-iframe'}
          />
        )}
      </div>

      {footer && (
        <div className={`footer grid grid-flex grid-justify-center ${animationClass} `} style={contentAnimationStyles}>
          {footer}
        </div>
      )}

      {userFirstInteraction && (
        <div className={'legal-note-container grid grid-flex grid-justify-center'}>
          <div
            data-testid='step-template-legal-note'
            className={`
              ${animationClass} align-center ${`
              ${
                externalIntegration
                  ? `admin-tool-legal-note margin-top-xxl`
                  : `admin-tool-legal-note-center margin-top-xxl`
              }`} padding-horizontal-m `}
            style={contentAnimationStyles}
          >
            <Font color={theme.text ? (theme.text as FontsProps['color']) : 'lightgray'}>
              {legalNoteContent ? legalNoteContent : DEFAULT_LEGAL_NOTE}
            </Font>
            {externalIntegration && (
              <div className={'admin-tool-teaser'}>
                <Font color={theme.text ? (theme.text as FontsProps['color']) : 'lightgray'}>Ein Service von</Font>
                <div style={{ display: 'inline-block', paddingLeft: '4px' }}>
                  <img
                    src='https://www.static-immobilienscout24.de/statpic/statpic/sales-journey/hos/rle-wizard/icons/f6b2e1034f1fc5163f885787761f93f6_PoweredByTeaser.svg'
                    alt='teaser'
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

StepTemplate.defaultProps = {
  isNextEnabled: false,
  displayNext: true,
  displayBack: true,
  goToNextStep: () => {},
}

export default StepTemplate
